// ═══════════════════════════════════════════════════════════════════════════════
// MXDR365 — Sidebar Navigation
// Role-aware, collapsible. Shows the real signed-in Entra ID user.
// ═══════════════════════════════════════════════════════════════════════════════
import { useState, CSSProperties, ReactNode } from 'react';
import { useApp, useEnsureArticles } from '../../hooks/useAppState';
import { theme, darkTheme } from '../../lib/theme';
import { Badge, Avatar, Icons } from '../shared';
import { useDisplayNameLookup } from '../../hooks/useDisplayName';
import { resolveDisplayName } from '../../lib/displayName';
import type { ViewId } from '../../types';
import { getAllowedItemIds, isGmView, isKnownUserRole } from '../../config/featureVisibility';

// ─── SVG Icon Components (stroke-based, 16×16) ───
const svgProps = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

const icons: Record<string, ReactNode> = {
  home:        <svg {...svgProps}><path d="M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/><path d="M9 21V14h6v7"/></svg>,
  kb:          <svg {...svgProps}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5V4.5A2.5 2.5 0 0 1 6.5 2z"/><path d="M8 7h8"/><path d="M8 11h6"/></svg>,
  kql:         <svg {...svgProps}><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>,
  verify:      <svg {...svgProps}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01z"/></svg>,
  mitre:       <svg {...svgProps}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  decisions:   <svg {...svgProps}><circle cx="12" cy="5" r="2"/><circle cx="6" cy="19" r="2"/><circle cx="18" cy="19" r="2"/><path d="M12 7v4l-6 6"/><path d="M12 11l6 6"/></svg>,
  ai:          <svg {...svgProps}><path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"/><path d="M16 14H8a4 4 0 0 0-4 4v2h16v-2a4 4 0 0 0-4-4z"/><circle cx="9" cy="7" r="0.5" fill="currentColor" stroke="none"/><circle cx="15" cy="7" r="0.5" fill="currentColor" stroke="none"/></svg>,
  handoff:     <svg {...svgProps}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
  customers:   <svg {...svgProps}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 21V9h6v12"/><path d="M3 9h18"/></svg>,
  onboarding:  <svg {...svgProps}><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/></svg>,
  glossary:    <svg {...svgProps}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5V4.5A2.5 2.5 0 0 1 6.5 2z"/><path d="M8 7h8"/><path d="M8 11h6"/></svg>,
  lms:         <svg {...svgProps}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5"/></svg>,
  projects:    <svg {...svgProps}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  tasks:       <svg {...svgProps}><path d="M9 11l3 3 8-8"/><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9"/></svg>,
  calendar:    <svg {...svgProps}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  workload:    <svg {...svgProps}><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/></svg>,
  admin:       <svg {...svgProps}><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
  users:       <svg {...svgProps}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  'signin-log': <svg {...svgProps}><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10,17 15,12 10,7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>,
  api_keys:     <svg {...svgProps}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>,
  // SOC Manager (Cat 1-3)
  sla:         <svg {...svgProps}><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  escalations: <svg {...svgProps}><path d="M12 19V5M5 12l7-7 7 7"/></svg>,
  ir:          <svg {...svgProps}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  threatHunting: <svg {...svgProps}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><path d="M11 8v6"/><path d="M8 11h6"/></svg>,
  engineering: <svg {...svgProps}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  provisioning: <svg {...svgProps}><path d="M12 2l7 4v6c0 4-3 7-7 8-4-1-7-4-7-8V6z"/><path d="M9.5 12l1.8 1.8 3.2-3.6"/></svg>,
  roster:      <svg {...svgProps}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/></svg>,
  checklist:   <svg {...svgProps}><path d="M9 11l3 3 8-8"/><rect x="3" y="3" width="18" height="18" rx="2"/></svg>,
  coverage:    <svg {...svgProps}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  qa:          <svg {...svgProps}><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/><path d="M16.24 7.76a6 6 0 0 1 0 8.49"/></svg>,
  // Cat 4-8
  reports:      <svg {...svgProps}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10,9 9,9 8,9"/></svg>,
  freshservice: <svg {...svgProps}><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><path d="M7 8h.01M11 8h.01M15 8h.01"/></svg>,
  portal:       <svg {...svgProps}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/></svg>,
  tickets:      <svg {...svgProps}><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/></svg>,
  // General Management (RMS→Hub integration)
  gm:           <svg {...svgProps}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  traction:     <svg {...svgProps}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  hiring:       <svg {...svgProps}><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>,
  discipline:   <svg {...svgProps}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>,
};

interface NavItem {
  id: ViewId;
  label: string;
  icon: ReactNode;
  adminOnly?: boolean;
  badge?: () => number | null;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

export function Sidebar() {
  const { state, dispatch, isAdmin, navigate, canViewView } = useApp();
  const t = theme;
  const [kbExpanded, setKbExpanded] = useState(true);

  // Sidebar is always mounted on authenticated views — using it as the
  // load trigger means article summaries arrive shortly after first paint
  // for every signed-in user without blocking the boot Promise.allSettled.
  useEnsureArticles();

  const unreadCount = state.notifications.filter(n => !n.read).length;
  const lookupName = useDisplayNameLookup();
  // The current user's name should already be the real display name (set by
  // /api/me), but resolve through the directory so that any UPN-looking
  // fallback also gets cleaned up at the highest-visibility surface.
  const currentUserName = resolveDisplayName(state.user.id, state.registeredUsers, state.user.name)
    || (state.user.name?.includes('@') ? state.user.name.split('@')[0] : state.user.name);

  const NAV_SECTIONS: NavSection[] = [
    {
      title: 'KNOWLEDGE BASE',
      items: [
        { id: 'home', label: 'Home', icon: icons.home },
        { id: 'kb', label: 'KB Articles', icon: icons.kb },
        { id: 'kql', label: 'KQL Library', icon: icons.kql },
        // Verification Queue — hidden for now (unused). The page, route, and
        // RBAC wiring are all still in place; uncomment to bring it back.
        // {
        //   id: 'verify', label: 'Verification Queue', icon: icons.verify,
        //   badge: () => {
        //     const count = state.articles.filter(a => {
        //       if (a.status !== 'published') return false;
        //       if (!a.verifiedAt) return true;
        //       const days = (Date.now() - new Date(a.verifiedAt).getTime()) / 86400000;
        //       return days > a.reviewCycleDays - 14;
        //     }).length;
        //     return count > 0 ? count : null;
        //   },
        // },
        { id: 'mitre', label: 'ATT&CK Coverage', icon: icons.mitre },
        { id: 'decisions', label: 'Decision Trees', icon: icons.decisions },
        { id: 'ai', label: 'AI Assistant', icon: icons.ai },
        { id: 'glossary', label: 'Glossary', icon: icons.glossary },
      ],
    },
    {
      title: 'SOC OPERATIONS',
      items: [
        { id: 'handoff', label: 'Shift Handoff', icon: icons.handoff },
        { id: 'customers', label: 'Client Management', icon: icons.customers },
        { id: 'freshservice', label: 'FreshService', icon: icons.freshservice },
        { id: 'sla', label: 'SLA Dashboard', icon: icons.sla },
        { id: 'escalations', label: 'Escalations', icon: icons.escalations },
        // Shift Roster was slated for retirement as a dormant duplicate, but
        // #537 rebuilt it as the assignments-sync-driven view — it stays.
        { id: 'roster', label: 'Shift Roster', icon: icons.roster },
        { id: 'coverage', label: 'Shift Coverage', icon: icons.coverage },
        { id: 'checklist', label: 'Daily Checklist', icon: icons.checklist },
      ],
    },
    {
      // Customer-facing portal — the ONLY section customer-role users see.
      // Staff (lead/admin) see it too, for previewing what customers get.
      title: 'CUSTOMER PORTAL',
      items: [
        { id: 'customerPortal', label: 'Customer Portal', icon: icons.portal },
      ],
    },
    {
      title: 'TIER THREE',
      items: [
        {
          id: 'ir', label: 'Incident Response', icon: icons.ir,
          badge: () => {
            const count = state.incidentResponses.filter(c => c.status !== 'closed').length;
            return count > 0 ? count : null;
          },
        },
        { id: 'threatHunting', label: 'Threat Hunting', icon: icons.threatHunting },
        { id: 'engineering', label: 'Engineering', icon: icons.engineering },
        { id: 'provisioning', label: 'Provisioning', icon: icons.provisioning },
        { id: 'onboarding', label: 'Customer Onboarding', icon: icons.onboarding },
      ],
    },
    {
      title: 'PEOPLE & QUALITY',
      items: [
        { id: 'roster-mgmt', label: 'Roster Management', icon: icons.roster },
        { id: 'qa', label: 'QA Scores', icon: icons.qa },
      ],
    },
    {
      // General Management — GM-tier surface (RMS→Hub integration, Phase 1).
      // Lead/admin see it as unrestricted roles; GM-tier users are
      // force-granted in the item filter below regardless of base role.
      title: 'GENERAL MANAGEMENT',
      items: [
        { id: 'gm', label: 'GM Dashboard', icon: icons.gm },
        { id: 'traction', label: 'EOS / Traction', icon: icons.traction },
        { id: 'hiring', label: 'Hiring', icon: icons.hiring },
        { id: 'people', label: 'People', icon: icons.users },
        { id: 'discipline', label: 'Discipline', icon: icons.discipline },
        { id: 'exec-reports', label: 'Executive Reports', icon: icons.reports },
      ],
    },
    {
      title: 'TRAINING',
      items: [
        { id: 'lms', label: 'Training (LMS)', icon: icons.lms },
      ],
    },
    {
      title: 'PROJECTS',
      items: [
        { id: 'projects', label: 'All Projects', icon: icons.projects },
        { id: 'calendar', label: 'Calendar', icon: icons.calendar },
        { id: 'tasks', label: 'Tasks', icon: icons.tasks },
        { id: 'workload', label: 'Workload', icon: icons.workload },
      ],
    },
    {
      title: 'REPORTING',
      items: [
        { id: 'reports', label: 'Reports & PDF Export', icon: icons.reports },
      ],
    },
    {
      title: 'ADMIN',
      items: [
        { id: 'admin', label: 'Admin Panel', icon: icons.admin, adminOnly: true },
        { id: 'users', label: 'User Management', icon: icons.users, adminOnly: true },
        { id: 'qa_admin', label: 'QA Administration', icon: icons.qa, adminOnly: true },
        { id: 'signin-log', label: 'Sign-In Log', icon: icons['signin-log'], adminOnly: true },
      ],
    },
    {
      title: 'SETTINGS',
      items: [
        { id: 'api_keys', label: 'API Keys', icon: icons.api_keys },
      ],
    },
  ];

  if (!state.sidebarOpen) return null;

  // Active users in last 90s — server-side presence (cross-device).  Falls
  // back to BroadcastChannel `presence` if the heartbeat hasn't loaded yet.
  const cutoff = Date.now() - 90_000;
  const serverOthers = state.onlineUsers.filter(p => p.userId !== state.user.id && p.lastSeen > cutoff);
  const localOthers = state.presence.filter(p => p.userId !== state.user.id && p.lastSeen > Date.now() - 60_000);
  const seen = new Set<string>();
  const activeOthers = [...serverOthers, ...localOthers].filter(p => {
    if (seen.has(p.userId)) return false;
    seen.add(p.userId);
    return true;
  });

  return (
    <aside style={{
      width: 240, background: t.sidebar, borderRight: `1px solid ${t.sidebarBorder}`,
      display: 'flex', flexDirection: 'column', height: '100vh',
      position: 'sticky', top: 0, flexShrink: 0, overflowY: 'auto', overflowX: 'hidden',
    }}>
      {/* Brand accent line */}
      <div style={{ height: 3, background: `linear-gradient(90deg, ${t.sidebarAccent} 0%, ${t.accentDark} 60%, transparent 100%)`, flexShrink: 0 }} />

      {/* Logo — official horizontal lockup, reversed (light) color variant:
          the sidebar surface is dark in BOTH themes, so the white+red
          treatment of the same logo is used here; the primary navy/red art
          renders on light surfaces (PDFs, emails). */}
      <div style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${t.sidebarBorder}`, flexShrink: 0 }}>
        <img
          src="/brand/mxdr365-logo-horizontal-light.png"
          alt="MXDR365 — we have the watch"
          style={{ width: '100%', maxWidth: 190, height: 'auto', display: 'block', margin: '0 auto' }}
        />
      </div>

      {/* Signed-in user */}
      <div style={{ padding: '10px 12px', borderBottom: `1px solid ${t.sidebarBorder}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8 }}>
          <Avatar name={currentUserName} size={28} id={state.user.id} online />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: t.sidebarText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentUserName}</div>
            <div style={{ fontSize: 9, color: t.sidebarTextTertiary }}>
              {state.user.email || state.user.tier + ' · ' + state.user.shift} · {state.user.role}
            </div>
          </div>
          {isAdmin && <Badge sm color={darkTheme.yellow}>Admin</Badge>}
        </div>
        {/* Role toggle — admins can switch to analyst view and back */}
        {(isAdmin || state.originalUser?.role === 'admin') && (
          <div style={{ display: 'flex', gap: 4, padding: '4px 8px 0' }}>
            {(['admin', 'analyst'] as const).map(r => (
              <button
                key={r}
                onClick={() => dispatch({ type: 'SWITCH_ROLE', role: r })}
                style={{
                  flex: 1,
                  padding: '3px 0',
                  fontSize: 9,
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  background: state.user.role === r ? t.sidebarAccent : 'transparent',
                  color: state.user.role === r ? '#0a1120' : t.sidebarTextTertiary,
                  transition: 'all 0.15s',
                }}
              >
                {r === 'admin' ? 'Admin View' : 'Analyst View'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Collaboration presence — internal staff only; never show analyst
          names/activity to customer-role users */}
      {state.user.role !== 'customer' && activeOthers.length > 0 && (
        <div style={{ padding: '6px 14px', borderBottom: `1px solid ${t.sidebarBorder}`, display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: darkTheme.green }} />
          <span style={{ fontSize: 9, color: t.sidebarTextSecondary }}>{activeOthers.length} other{activeOthers.length !== 1 ? 's' : ''} online</span>
          <div style={{ display: 'flex', gap: 3, marginLeft: 2 }}>
            {activeOthers.slice(0, 3).map(u => (
              <div key={u.userId} title={`${lookupName(u.userId, u.userName)}${u.view ? ' — ' + u.view : ''}`}>
                <Avatar name={lookupName(u.userId, u.userName)} size={14} id={u.userId} online />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation Sections */}
      <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
        {NAV_SECTIONS.map(section => {
          if (!isKnownUserRole(state.user.role)) return null;
          const baseAllowed = getAllowedItemIds(state.user.role, section.title, section.items.map(i => i.id));
          // Augment with RBAC custom-role canView grants — users assigned a custom
          // role with canView on a viewId see it even if their base role's
          // feature-visibility excludes it.
          const allowedSet = new Set<string>(baseAllowed || []);
          for (const item of section.items) {
            if (canViewView(item.id)) allowedSet.add(item.id);
            // GM-tier users always see the GENERAL MANAGEMENT views, matching
            // the navigate() guard in useAppState.
            if (state.user.tier === 'GM' && isGmView(item.id)) allowedSet.add(item.id);
          }
          const visibleItems = section.items.filter(item => {
            // Customer Portal is fully controlled by the server-side
            // CUSTOMER_PORTAL_ENABLED flag (echoed via /api/me).
            if (item.id === 'customerPortal' && !state.user.customerPortalEnabled) return false;
            return allowedSet.has(item.id) && (!item.adminOnly || isAdmin);
          });
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.title} style={{ marginBottom: 4 }}>
              <div style={{ padding: '8px 16px 4px', fontSize: 9, fontWeight: 700, color: t.sidebarTextTertiary, letterSpacing: 1 }}>
                {section.title}
              </div>
              {visibleItems.map(item => {
                const active = state.view === item.id;
                const badgeCount = item.badge ? item.badge() : null;
                const isKbItem = item.id === 'kb';
                const hasMultipleKbs = state.knowledgeBases.length > 1;

                return (
                  <div key={item.id}>
                    <div
                      onClick={() => {
                        if (isKbItem && hasMultipleKbs) {
                          setKbExpanded(!kbExpanded);
                        }
                        navigate(item.id);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 16px',
                        cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400,
                        color: active ? t.sidebarAccent : t.sidebarTextSecondary,
                        background: active ? t.sidebarAccentGlow : 'transparent',
                        borderRight: active ? `2px solid ${t.sidebarAccent}` : '2px solid transparent',
                        transition: 'all .1s',
                      }}
                    >
                      <span style={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.icon}</span>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {isKbItem && hasMultipleKbs && (
                        <span style={{ fontSize: 9, color: t.sidebarTextTertiary, transition: 'transform .15s', transform: kbExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                          <Icons.ChevronRight />
                        </span>
                      )}
                      {badgeCount !== null && <Badge sm color={darkTheme.yellow}>{badgeCount}</Badge>}
                      {item.id === 'freshservice' && state.freshServiceStats && state.freshServiceStats.open > 0 && (
                        <Badge sm color={t.blue}>{state.freshServiceStats.open}</Badge>
                      )}
                    </div>
                    {/* KB sub-items — show each knowledge base */}
                    {isKbItem && hasMultipleKbs && kbExpanded && (
                      <div>
                        {state.knowledgeBases.map(kb => {
                          const isActiveKb = kb.id === state.activeKnowledgeBaseId;
                          const kbArticleCount = state.articles.filter(a => (a.knowledgeBaseId || 'default') === kb.id && a.status === 'published').length;
                          return (
                            <div
                              key={kb.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                dispatch({ type: 'SET_ACTIVE_KB', kbId: kb.id });
                                navigate('kb');
                              }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '5px 16px 5px 44px',
                                cursor: 'pointer', fontSize: 11,
                                fontWeight: isActiveKb && active ? 600 : 400,
                                color: isActiveKb && active ? t.sidebarAccent : t.sidebarTextTertiary,
                                background: isActiveKb && active ? `${t.sidebarAccent}08` : 'transparent',
                                transition: 'all .1s',
                              }}
                            >
                              <span style={{
                                width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
                                background: isActiveKb ? t.sidebarAccent : t.sidebarTextTertiary,
                                opacity: isActiveKb ? 1 : 0.4,
                              }} />
                              <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{kb.name}</span>
                              <span style={{ fontSize: 9, color: t.sidebarTextTertiary, fontFamily: 'monospace' }}>{kbArticleCount}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ padding: '10px 16px', borderTop: `1px solid ${t.sidebarBorder}`, flexShrink: 0, fontSize: 9, color: t.sidebarTextTertiary }}>
        <div>v1.1.0 · Patriot Consulting</div>
        <div style={{ color: t.sidebarAccent, marginTop: 2 }}>Powered by MXDR365</div>
        <div style={{ marginTop: 4, color: t.sidebarTextTertiary }}>
          Theme: {state.themeSchedule?.mode ?? 'dark'}
        </div>
      </div>
    </aside>
  );
}
